import { useLocation, useNavigate, useParams } from "react-router-dom";
import { YStack, XStack, View, Text } from "tamagui";
import { RiFileList3Line, RiArrowRightSLine } from "react-icons/ri";

import SkinV2Screen from "@components/layout/SkinV2Screen";
import { v2 } from "@screen/Home/pages/GasShop/skin";
import ReceiptPaper from "@components/layout/ReceiptPaper";
import { colors } from "@styles/colors";

import SectionLabel from "./components/ui/SectionLabel";
import InfoNote from "./components/ui/InfoNote";
import { orderStatusLabel, paymentMethodLabel, type PaymentRecordItem } from "./API/handleAccountHistoryAPI";
import { isOrderAmountHidden } from "@utils/func/orderAmountVisibility";

const fmt = (iso: string): string => {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

/**
 * 付費明細。資料由列表頁透過 router state 帶入（payment-records 無單筆明細 endpoint）。
 */
export const PayRecordDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { paymentId } = useParams();

  const navState = location.state as { item?: PaymentRecordItem; from?: string } | null;
  const item = navState?.item ?? null;
  // 來源頁決定返回去處（帳號頁進來 → 回帳號頁；否則回付費紀錄列表）
  const back = () => navigate(navState?.from ?? "/account/payRecord");

  if (!item) {
    return (
      <SkinV2Screen title="付費明細" onBack={back}>
        <YStack f={1} ai="center" jc="center" gap="$2">
          <Text fontSize={14} color={v2.body}>
            查無此筆付費紀錄
          </Text>
          <Text fontSize={12} color={v2.sub}>
            請從付費紀錄列表點入（{paymentId ?? "-"}）
          </Text>
        </YStack>
      </SkinV2Screen>
    );
  }

  const accent = colors.primary;

  /* 這頁的核心事實（金額、付款方式、單號、時間）做成一張印出來的收據，
     與下單成功頁同一張紙（見 components/layout/ReceiptPaper）。
     藍色帶上只留一行脈絡：金額已經是紙上的主秀，再放一次會變成同一個數字出現兩遍。 */
  const subHeader = (
    <YStack mb={16}>
      <Text color={v2.hazeSub} fontSize={13}>
        {orderStatusLabel(item.order_status)}
      </Text>
    </YStack>
  );

  const receiptRows = [
    { k: "付款方式", v: paymentMethodLabel(item.payment_method_label) },
    { k: "所屬帳戶", v: item.display_title },
    { k: "訂單編號", v: item.display_id ?? "—" },
    { k: "時間", v: fmt(item.paid_at ?? item.created_at) },
  ];

  return (
    <SkinV2Screen title="付費明細" onBack={back} subHeader={subHeader}>
      <YStack className="gas-stagger-kids" gap="$3.5">
        <ReceiptPaper
          // 該瓦斯行開啟隱藏金額設定且訂單待確認 → 金額尚未定案，以文字取代數字
          amount={isOrderAmountHidden(item.hide_amount_on_pending, item.order_status) ? null : item.amount ?? 0}
          fallbackTitle="待確認"
          rows={receiptRows}
        />

        {/* 關聯訂單：與上方收據之間多留 10px（收據下緣是鋸齒／陰影，貼太近會讀成同一塊） */}
        <YStack mt={10}>
          <SectionLabel title="關聯訂單" />
          <XStack
            bg={v2.card}
            br={24}
            px="$4"
            py="$3.5"
            ai="center"
            gap="$3"
            cursor="pointer"
            pressStyle={{ opacity: 0.7 }}
            style={{ boxShadow: "0 2px 10px -4px rgba(21, 101, 192, 0.10)" }}
            onPress={() => navigate(`/orders/${item.platform_order_id}`, { state: { xContext: `context:${item.context_id}` } })}>
            <View w={38} h={38} br={10} bg={`${accent}15`} ai="center" jc="center" flexShrink={0}>
              <RiFileList3Line size={18} color={accent} />
            </View>
            <YStack f={1} miw={0}>
              <Text fontSize={14} fontWeight="700" color={v2.title}>
                查看此訂單
              </Text>
              <Text fontSize={11} color={v2.sub} mt="$0.5" numberOfLines={1}>
                {item.display_id ?? "—"}
              </Text>
            </YStack>
            <RiArrowRightSLine size={16} color={accent} />
          </XStack>
        </YStack>

        <InfoNote>金額為下單當下凍結值。如需電子發票或報帳，請於訂單頁操作。</InfoNote>
      </YStack>
    </SkinV2Screen>
  );
};

export default PayRecordDetail;
